// src/components/SocialLogin.tsx
import React, { useCallback, useState } from 'react';
import { SITE_URL, socialLoginAuthUrl } from '../../configurations/api/url';
import CustomButton from '../Button/CustomButton';
import { useDispatchPromise } from '../../utils/AppUtils';
import { socialLogin } from '../../redux/authentication/action';
import { SentryCapture } from '../../analytics/Sentry';

type TProvider = 'meta' | 'tiktok' | 'youtube';

interface ISocialLogin {
    provider: TProvider;
    onAuthComplete?: (success: boolean, data?: any) => void;
    customButton?: JSX.Element;
    onClick?: () => void;
}

const SocialLogin: React.FC<ISocialLogin> = ({ provider, onAuthComplete, customButton, onClick }) => {
    const dispatch = useDispatchPromise();
    const [m, sm] = useState(`Login with ${provider.charAt(0).toUpperCase() + provider.slice(1)}`);

    const exchange = useCallback(
        (code: string) => {
            if (code) {
                dispatch(socialLogin({ code, provider }))
                    .then((response: any) => {
                        onAuthComplete?.(true, response.data); // success callback
                    })
                    .catch((error: any) => {
                        onAuthComplete?.(false, null);
                        SentryCapture(error);
                    });
            }
        },
        [dispatch, provider, onAuthComplete],
    );

    const openPopup = () => {
        const popup = window.open(`${socialLoginAuthUrl}/${provider}`, `${provider} Login`, 'width=600,height=700');
        onClick && onClick();
        const handleMessage = (event: MessageEvent) => {
            const expectedOrigin = new URL(SITE_URL).origin;

            if (event.origin !== expectedOrigin) return;
            if (!event.data?.type) return;

            if (event.data.type === 'SOCIAL_AUTH_SUCCESS') {
                if (event.data.provider === provider && event.data.code) {
                    window.removeEventListener('message', handleMessage);
                    sm('Completed');
                    clearInterval(timer);
                    exchange(event.data.code);
                }
            }

            if (event.data.type === 'SOCIAL_AUTH_FAILED') {
                window.removeEventListener('message', handleMessage);
                clearInterval(timer);
                onAuthComplete?.(false);
            }
        };

        window.addEventListener('message', handleMessage);

        const timer = setInterval(() => {
            if (popup?.closed) {
                clearInterval(timer);
                window.removeEventListener('message', handleMessage);
            }
        }, 500);
    };

    return customButton ? (
        React.cloneElement(customButton, { onClick: openPopup })
    ) : (
        <CustomButton onClick={openPopup} label={m} />
    );
};

export { SocialLogin };
